import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import type { ResumeDto } from "@reactive-resume/dto";
import type { CustomSectionGroup, ResumeData, SectionKey } from "@reactive-resume/schema";
import { defaultResumeData, defaultSection } from "@reactive-resume/schema";
import { removeItemInLayout } from "@reactive-resume/utils";
import _set from "lodash.set";
import type { TemporalState } from "zundo";
import { temporal } from "zundo";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useStoreWithEqualityFn } from "zustand/traditional";

import { debouncedUpdateResume } from "../services/resume";

type ResumeStore = {
  resume: ResumeDto;

  // Actions
  setValue: (path: string, value: unknown) => void;
  setData: (data: ResumeData) => void;

  // Custom Section Actions
  addSection: () => void;
  removeSection: (sectionId: SectionKey) => void;
};

const cleanForUpdate = (resume: any) => {
  const clone = JSON.parse(JSON.stringify(resume));
  const items = clone?.data?.sections?.experience?.items;
  if (Array.isArray(items)) {
    clone.data.sections.experience.items = items.map((it: any) => {
      if (it.contractType === "") {
        const { contractType, ...rest } = it;
        return rest;
      }
      return it;
    });
  }
  return clone;
};

export const useResumeStore = create<ResumeStore>()(
  temporal(
    immer((set) => ({
      resume: {
        data: JSON.parse(JSON.stringify(defaultResumeData)),
      } as unknown as ResumeDto,
      setData: (data) => {
        set((state) => {
          state.resume.data = data;
        });
        void debouncedUpdateResume(cleanForUpdate(useResumeStore.getState().resume));
      },
      setValue: (path, value) => {
        set((state) => {
          if (!state.resume.data) {
            state.resume.data = JSON.parse(JSON.stringify(defaultResumeData));
          }
          if (path === "visibility") {
            state.resume.visibility = value as "public" | "private";
          } else {
            state.resume.data = _set(state.resume.data, path, value);
          }
        });

        void debouncedUpdateResume(cleanForUpdate(useResumeStore.getState().resume));
      },
      addSection: () => {
        const section: CustomSectionGroup = {
          ...defaultSection,
          id: createId(),
          name: t`Custom Section`,
          items: [],
        };

        set((state) => {
          const lastPageIndex = state.resume.data.metadata.layout.length - 1;
          state.resume.data.metadata.layout[lastPageIndex][0].push(`custom.${section.id}`);
          state.resume.data = _set(state.resume.data, `sections.custom.${section.id}`, section);
        });

        void debouncedUpdateResume(cleanForUpdate(useResumeStore.getState().resume));
      },
      removeSection: (sectionId: SectionKey) => {
        if (sectionId.startsWith("custom.")) {
          const id = sectionId.split("custom.")[1];

          set((state) => {
            removeItemInLayout(sectionId, state.resume.data.metadata.layout);
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete state.resume.data.sections.custom[id];
          });

          void debouncedUpdateResume(cleanForUpdate(useResumeStore.getState().resume));
        }
      },
    })),
    {
      limit: 100,
      wrapTemporal: (fn) => devtools(fn),
      partialize: ({ resume }) => ({ resume }),
    },
  ),
);

export const useTemporalResumeStore = <T>(
  selector: (state: TemporalState<Pick<ResumeStore, "resume">>) => T,
  equality?: (a: T, b: T) => boolean,
) => useStoreWithEqualityFn(useResumeStore.temporal, selector, equality);
